import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import App from './App.vue'
import router from './router'
import i18n from './plugins/i18n'
import { useAuthStore } from './stores/auth'
import './assets/main.css'

// Configure passive event listeners for better scroll performance
// This prevents the Chrome warning about non-passive touch event listeners
if (typeof window !== 'undefined') {
  const supportsPassive = (() => {
    let passive = false
    try {
      const opts = Object.defineProperty({}, 'passive', {
        get() {
          passive = true
          return passive
        },
      })
      window.addEventListener('test', null as any, opts)
      window.removeEventListener('test', null as any, opts)
    } catch (e) {
      // Passive not supported
    }
    return passive
  })()

  if (supportsPassive) {
    // Override addEventListener to make touch events passive by default
    const originalAddEventListener = EventTarget.prototype.addEventListener
    EventTarget.prototype.addEventListener = function (
      type: string,
      listener: any,
      options?: any
    ) {
      if (type === 'touchstart' || type === 'touchmove') {
        if (typeof options === 'boolean') {
          options = { capture: options, passive: true }
        } else if (typeof options === 'object' && options.passive === undefined) {
          options = { ...options, passive: true }
        } else if (!options) {
          options = { passive: true }
        }
      }
      return originalAddEventListener.call(this, type, listener, options)
    }
  }
}

const app = createApp(App)
const pinia = createPinia()
setActivePinia(pinia)
app.use(pinia)

const authStore = useAuthStore()

app.use(router)
app.use(i18n)

console.log('[main.ts] Initializing auth store...')

// Add timeout to prevent infinite hanging
const initTimeout = new Promise((_, reject) =>
  setTimeout(() => reject(new Error('Auth initialization timeout')), 5000)
)

Promise.race([authStore.initialize(), initTimeout])
  .then(() => {
    console.log('[main.ts] Auth store initialized successfully')

    // Add a router guard to protect routes requiring authentication
    // CRITICAL: Pass isAuthReady to prevent race conditions in production builds
    import('./router/authGuard').then(({ guardDecision }) => {
      console.log('[main.ts] Auth guard loaded')
      router.beforeEach((to, from) =>
        guardDecision(authStore.user, to, from, authStore.isAuthReady)
      )
    })

    console.log('[main.ts] Mounting app...')
    app.mount('#app')
    console.log('[main.ts] App mounted successfully!')
  })
  .catch((error) => {
    console.error('[main.ts] Auth initialization failed:', error)
    console.log('[main.ts] Mounting app anyway to prevent hang...')

    // Mount app anyway to prevent complete hang
    import('./router/authGuard').then(({ guardDecision }) => {
      router.beforeEach((to, from) =>
        guardDecision(authStore.user, to, from, authStore.isAuthReady)
      )
    }).finally(() => {
      app.mount('#app')
      console.log('[main.ts] App mounted (fallback after error)')
    })
  })



